import { accountsSelectors } from 'app-components/flux/accounts';
import { selectShouldHideBusinessFromAiChat } from 'app-components/flux/ai-chat/aiChatSelectors';
import { type getBusinessCoaIdsSet, getCategoriesById } from 'app-components/flux/categories/categoriesSelectors';
import { UsageTypeEnum } from 'app-components/flux/categories/categoriesTypes';
import { type ChartOfAccountProps, CoaTypeEnum } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { getCoaString } from 'app-components/flux/chart-of-accounts/chartOfAccountsUtils';
import { isSplitTxn } from 'app-components/flux/transactions/transactionsPredicates';
import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { isInvoiceRelatedTransaction } from 'app-components/flux/transactions/transactionsUtils';
import { numberUtils } from 'app-components/utils/number';
import type RootState from 'app-components/utils/redux-store/rootState';

import type { ChartDataRow, ChartRequest, ChartType } from '../aiChatTypes';
import type { LlmToolsCategoryDict, LlmToolsCategoryForAI } from './llmToolsTypes';

export const findCategoryIdByName = (state: RootState, categoryName: string): string | undefined => {
  if (!categoryName) return undefined;
  const categories = getCategoriesById(state);
  const normalizedName = categoryName.toLowerCase().trim();
  const found = categories.find((cat) => cat.name?.toLowerCase().trim() === normalizedName);
  return found?.id;
};

// Simplified COA type for LLM responses - maps CoaTypeEnum.ACCOUNT to TRANSFER for clarity,
// and excludes internal types (CATEGORY_USAGE_TYPE, DISPLAY_NODE, NONE) not relevant to transactions
export type CoaInfoType = 'CATEGORY' | 'TRANSFER' | 'BALANCE_ADJUSTMENT' | 'UNCATEGORIZED' | 'SPLIT';

export type CoaInfo = {
  type: CoaInfoType;
  name: string;
  id?: string;
};

export type CoaLookupMaps = {
  categoriesById: ReturnType<typeof getCategoriesById>;
  accountsById: ReturnType<typeof accountsSelectors.getAccountsById>;
};

export const getCoaInfoWithMaps = (coa: ChartOfAccountProps | null | undefined, maps: CoaLookupMaps): CoaInfo => {
  if (!coa || coa.type === CoaTypeEnum.UNCATEGORIZED) {
    return { type: 'UNCATEGORIZED', name: 'Uncategorized' };
  }

  const baseName = getCoaString(coa, maps.categoriesById, maps.accountsById);

  switch (coa.type) {
    case CoaTypeEnum.ACCOUNT:
      return { type: 'TRANSFER', name: `Transfer: ${baseName}`, id: coa.id };
    case CoaTypeEnum.BALANCE_ADJUSTMENT:
      return { type: 'BALANCE_ADJUSTMENT', name: baseName, id: coa.id };
    case CoaTypeEnum.CATEGORY:
      return { type: 'CATEGORY', name: baseName, id: coa.id };
    default:
      return { type: 'UNCATEGORIZED', name: 'Uncategorized' };
  }
};

const BUSINESS_USAGE_TYPES = new Set<string>([UsageTypeEnum.BUSINESS, UsageTypeEnum.RENTAL, UsageTypeEnum.FARMING]);

export const isBusinessTransaction = (
  txn: TransactionRecord,
  businessCoaIds: ReturnType<typeof getBusinessCoaIdsSet>,
): boolean =>
  isInvoiceRelatedTransaction(txn) ||
  (txn.coa?.type === 'CATEGORY' && txn.coa.id != null && businessCoaIds.has(txn.coa.id));

export const buildArAccountIds = (state: RootState): Set<string> =>
  accountsSelectors.getAllArAccounts(state).reduce((acc, account) => {
    if (account.id) acc.add(account.id);
    return acc;
  }, new Set<string>());

export const isInvestmentTransaction = (txn: TransactionRecord): boolean => txn.type === 'INVESTMENT';

export const isEstimatedTransaction = (txn: TransactionRecord): boolean => txn.subtype === 'ESTIMATE';

export const isSplitTransaction = (txn: TransactionRecord): boolean => isSplitTxn(txn);

export const getCoaInfo = (state: RootState, coa: ChartOfAccountProps | null | undefined): CoaInfo => {
  const maps: CoaLookupMaps = {
    categoriesById: getCategoriesById(state),
    accountsById: accountsSelectors.getAccountsById(state),
  };
  return getCoaInfoWithMaps(coa, maps);
};

export const findAccountIdByName = (state: RootState, accountName: string): string | undefined => {
  if (!accountName) return undefined;
  const accounts = accountsSelectors.getAccountsById(state);
  const normalizedName = accountName.toLowerCase().trim();
  const found = accounts.find((acct) => acct.name?.toLowerCase().trim() === normalizedName);
  return found?.id ?? undefined;
};

export function categoriesForAI(
  input: LlmToolsCategoryDict,
  excludeBusinessCategories = false,
): LlmToolsCategoryForAI[] {
  return Object.entries(input).reduce<LlmToolsCategoryForAI[]>((acc, [key, cat]) => {
    if (!cat) return acc;

    const { isDeleted, isNotUserAssignable, isExcludedFromCategoryList, name, parentId, usageType } = cat;

    // filter out unwanted categories or missing name
    if (isDeleted || isNotUserAssignable || isExcludedFromCategoryList || !name) {
      return acc;
    }

    if (excludeBusinessCategories && BUSINESS_USAGE_TYPES.has(usageType ?? '')) {
      return acc;
    }

    acc.push({
      id: cat.id ?? key, // fall back to the map key if id is absent
      name,
      parentId: parentId ?? null,
    });

    return acc;
  }, []);
}

// Shared by transaction-actions capabilities (recategorize, cleanup_transaction) that need the
// same business-filtered category list for AI proposals - see
// docs/features/transaction-actions/README.md for why cross-capability logic like this lives
// in a shared home rather than being copied into each feature module.
export const getAllowedCategoriesForAI = (state: RootState): LlmToolsCategoryForAI[] => {
  const categoriesById = getCategoriesById(state);
  const excludeBusinessCategories = selectShouldHideBusinessFromAiChat(state);

  return categoriesForAI(categoriesById.toJS() as LlmToolsCategoryDict, excludeBusinessCategories);
};

export const toFiniteNumberOrNull = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null;
  }

  const numericValue = numberUtils.noNaN(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export const areAllValuesZero = (values: unknown[]): boolean => {
  const numericValues = values.reduce<number[]>((acc, value) => {
    const num = toFiniteNumberOrNull(value);
    if (num !== null) acc.push(num);
    return acc;
  }, []);

  return numericValues.length > 0 && numericValues.every((value) => value === 0 || Object.is(value, -0));
};

export const MIN_CHART_DATA_POINTS = 2;

export type ToolOutput = Record<string, unknown>;

type ChartTransformerConfig = {
  extractItems: (output: ToolOutput) => ToolOutput[];
  validationFields: (row: ToolOutput) => unknown[];
  mapRow: (row: ToolOutput) => ChartDataRow | null;
  chartType: ChartType;
  title: string | ((output: ToolOutput, data: ChartDataRow[]) => string);
  categoryField: string;
  valueFields: string[] | ((data: ChartDataRow[]) => string[]);
};

export const isRecord = (value: unknown): value is ToolOutput =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const buildChartTransformer = (config: ChartTransformerConfig): ((output: unknown) => ChartRequest | null) => {
  return (output: unknown): ChartRequest | null => {
    if (!isRecord(output)) return null;

    const items = config.extractItems(output);
    if (items.length < MIN_CHART_DATA_POINTS) return null;

    const values = items.flatMap(config.validationFields);
    if (areAllValuesZero(values)) return null;

    const data = items.reduce<ChartDataRow[]>((acc, item) => {
      const row = config.mapRow(item);
      if (row) acc.push(row);
      return acc;
    }, []);

    if (data.length < MIN_CHART_DATA_POINTS) return null;

    return {
      chartType: config.chartType,
      title: typeof config.title === 'function' ? config.title(output, data) : config.title,
      categoryField: config.categoryField,
      valueFields: typeof config.valueFields === 'function' ? config.valueFields(data) : config.valueFields,
      data,
    };
  };
};
